// packages/whiteboard/src/features/person/person-options.config.ts

import {
  FILL_ACTIONS,
  BORDER_ACTIONS,
  BORDER_WIDTH_ACTIONS,
  BORDER_STYLE_ACTIONS,
  BOX_SHADOW_ACTIONS,
  BACKGROUND_OPACITY_ACTIONS,
  Z_ORDER_ACTIONS,
  ALIGN_ACTIONS,
  BOARD_COMMAND_ACTIONS,
  TEXT_ACTIONS,
  TEXT_SIZE_ACTIONS,
  TEXT_WEIGHT_ACTIONS,
  FONT_STYLE_ACTIONS,
  TEXT_DECORATION_ACTIONS,
  type ActionIntent,
  type ActionsMap,
  type OptionsCircleDef,
} from '../../optionsbar/optionsbar-actions.js';
import { composeOptionsCircle } from '../../optionsbar/parts/compose-options-circle.js';

// Optionskreis aus Einzelteilen (Person = 1+2+3+5+6+10): Standard-Knöpfe +
// Rahmen/Linien-Optionen + Füllfarben + Eckenrundung + Personen-Marker +
// Schriftgrößen/Textfarben/Textoptionen.
const personSvg = composeOptionsCircle([1, 2, 3, 5, 6, 10]);
import { PERSON_ICON_ORDER } from './person-icons.config.js';

/**
 * Resolves an optionsbar action intent to a PersonObject property. The text
 * intents style the name label; `person-icon` picks the avatar motif. Borders,
 * effects and the sub-/superscript marks do NOT apply to a person and return
 * `null`.
 *
 * The `kreis` group of intents all recolour/shape the avatar's `id="kreis"`
 * background circle, never the name label: `fill` → its area (`circleFill`),
 * `border-color` → its ring (`circleStroke`), `border-width` → ring thickness
 * (`circleStrokeWidth`), `border-style` → ring line style (`circleStrokeStyle`),
 * `background-opacity` → fade the fill (`backgroundOpacity`) and `box-shadow` →
 * a drop shadow behind the whole avatar (`boxShadow`). All 8 avatars carry the
 * `kreis` circle, so these apply to every motif.
 */
function resolvePersonProperty(intent: ActionIntent): string | null {
  switch (intent) {
    case 'fill':
      return 'circleFill';
    case 'border-color':
      return 'circleStroke';
    case 'border-width':
      return 'circleStrokeWidth';
    case 'border-style':
      return 'circleStrokeStyle';
    case 'background-opacity':
      return 'backgroundOpacity';
    case 'box-shadow':
      return 'boxShadow';
    case 'person-icon':
      return 'icon';
    case 'text-color':
      return 'textColor';
    case 'text-size':
      return 'fontSize';
    case 'text-weight':
      return 'fontWeight';
    case 'font-style':
      return 'fontStyle';
    case 'text-decoration':
      return 'textDecoration';
    default:
      return null;
  }
}

/**
 * Person-icon buttons: SVG id `person-1`..`person-8` → the icon key at that
 * position (see PERSON_ICON_ORDER). Person-specific (not a shared map), but a
 * plain data mapping — no bespoke action logic.
 */
const PERSON_ICON_ACTIONS: ActionsMap = Object.fromEntries(
  PERSON_ICON_ORDER.map((key, i) => [
    `person-${i + 1}`,
    { intent: 'person-icon', value: key },
  ]),
);

/**
 * Person-specific optionsbar definition. Composed from shared maps plus the
 * person-icon selector — no bespoke action logic. Wired: the 8 icon buttons,
 * the avatar `kreis` circle's fill/border colour, ring width + style, its
 * background transparency and drop shadow, the name label's
 * colour/size/weight/italic/decoration, z-order and board commands.
 *
 * The SVG additionally draws the corner-radius and sub-/superscript groups. A
 * round avatar has no corners and `Konva.Text` has no vertical script, so those
 * stay inert graphics (kept out of `actions`) rather than being wired to
 * properties the model cannot honour.
 */
export const PERSON_OPTIONS: OptionsCircleDef = {
  svg: personSvg,
  actions: {
    ...FILL_ACTIONS,
    ...BORDER_ACTIONS,
    ...BORDER_WIDTH_ACTIONS,
    ...BORDER_STYLE_ACTIONS,
    ...BOX_SHADOW_ACTIONS,
    ...BACKGROUND_OPACITY_ACTIONS,
    ...PERSON_ICON_ACTIONS,
    ...TEXT_ACTIONS,
    ...TEXT_SIZE_ACTIONS,
    ...TEXT_WEIGHT_ACTIONS,
    ...FONT_STYLE_ACTIONS,
    ...TEXT_DECORATION_ACTIONS,
    ...Z_ORDER_ACTIONS,
    ...ALIGN_ACTIONS,
    ...BOARD_COMMAND_ACTIONS,
  },
  resolveProperty: resolvePersonProperty,
};
